import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import '@ant-design/compatible/assets/index.css';
import { Layout, Button } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE } from '../../utils/DataConstants';
import { floatToPercentageString } from '../../utils/DataFormatterUtils';
import OWDateRangePicker from '../ow_date_range_picker/OWDateRangePicker';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS } from '../../actions/site_surveys_locationandwalkthrough_actions';
import { siteSurveysLocationAndWalkthroughDetail } from '../../thunks/site_surveys_grouped_thunks';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import DateTimeHover from '../date_time_component/DateTime';
import BuyerRegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { withUserType } from '../withUserType';
import { safeEncodeURIComponent } from '../../utils/HistoryUtils';

const { Content } = Layout;

const SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION = 'IndexPage';

function SiteSurveysWalkThroughWithLocation(props) {
	const { siteSurveys, locations, userType } = props;

	const location = useLocation();
	const history = useHistory();
	const { title: siteSurveyTitle, templateId: siteSurveyTemplateId } = useParams<{
		title: string;
		templateId: string;
	}>();

	const isArchivedTab = location.pathname.split('/').pop() === 'archivedLocations';

	const columns = React.useMemo(
		() => [
			{
				title: 'Location',
				dataIndex: 'locationName',
				key: 'locationName',
				sorter: (a, b) => a.locationName.localeCompare(b.locationName),
			},
			{
				title: 'Total Walkthroughs',
				dataIndex: 'totalWalkthroughs',
				key: 'totalWalkthroughs',
				sorter: (a, b) => a.totalWalkthroughs - b.totalWalkthroughs,
			},
			{
				title: 'Average Walkthrough Score',
				dataIndex: 'averageWalkthroughScore',
				key: 'averageWalkthroughScore',
				sorter: (a, b) => a.averageWalkthroughScore - b.averageWalkthroughScore,
				render: (_, record) => (parseFloat(_) && floatToPercentageString(_)) || '--',
			},
			{
				title: 'Last Walkthrough Completed',
				dataIndex: 'lastWalkthroughCompleted',
				key: 'lastWalkthroughCompleted',
				render: (_, record) =>
					_ ? (
						<DateTimeHover
							timestamp={_}
							showDate
							timeZone={nullSafeGet('locationTimeZoneId', record)}
						/>
					) : (
						'--'
					),
			},
		],
		[]
	);

	const filterConfig = React.useMemo(
		() => [
			{
				label: 'Location',
				fieldName: 'locationIds',
				mode: 'multiple',
				elementDefined: true,
				stateSlice: locations,
				keyAccessor: (loc) => loc.id,
				valueAccessor: (loc) => loc.id,
				targetCollectionName: 'siteSurveysAdvancedFilters',
				renderItem: (loc) => loc.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: BuyerRegionsLocationsTreeSelect,
			},
			{
				label: 'Walkthrough completed between',
				labelPrefix: 'Walkthrough completed',
				fieldName: FILTER_CONFIG_NAMES.WALKTHROUGH_COMPLETED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
			},
			{
				label: 'Walkthrough created between',
				labelPrefix: 'Walkthrough created',
				fieldName: FILTER_CONFIG_NAMES.CREATED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
			},
		],
		[locations]
	);

	const onRow = React.useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				let backUrl = `/${userType}/siteSurveys/detail/${record.siteSurveyId}/details`;
				if (searchString && searchString.length > 0) {
					backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
				}
				history.push(backUrl);
			},
		}),
		[location.search, history, userType]
	);

	return (
		<Content className="siteSurveysPage" style={{ padding: '0 0.5em' }}>
			<ReduxDataTable
				rightActions={
					!isArchivedTab ? (
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
							<Button
								type="primary"
								size="large"
								onClick={() =>
									history.push(
										`/${userType}/siteSurveys/byTitle/${safeEncodeURIComponent(
											siteSurveyTitle
										)}/template/${safeEncodeURIComponent(siteSurveyTemplateId)}/addLocation`
									)
								}
							>
								Add Locations
							</Button>
						</AccessPermissionChecker>
					) : null
				}
				handleQueryParams
				filterConfig={filterConfig}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Currently there are no site surveys!"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any site surveys yet.
								</div>
							</div>
						}
					/>
				}
				onTableRow={onRow}
				showHeader
				allDataAtOnce
				targetCollectionName={SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION}
				tableColumns={columns}
				keyAccessor={(el) => el.id}
				initialPagination={{ current: 1, pageSize: 10 }}
				hiddenPagination={{ showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
				fetchData={siteSurveysLocationAndWalkthroughDetail(userType)}
				stateSlice={siteSurveys}
				entityCollectionName="site_surveys/locationandwalkthrough"
				initialSorting={{ sort_by: 'locationName', order: 'ascend' }}
				preAppliedFilters={{
					title: siteSurveyTitle,
					templateId: siteSurveyTemplateId,
					isArchived: isArchivedTab,
				}}
			/>
		</Content>
	);
}

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	siteSurveys: state['site_surveys/locationandwalkthrough'],
	locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
	updateSiteSurveyFilters: (filters, targetCollection) =>
		dispatch(
			SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.updateFilters(
				filters,
				targetCollection
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			SITE_SURVEYS_LOCATIONANDWALKTHROUGH_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(SiteSurveysWalkThroughWithLocation))
);
